import React, { ReactNode, useContext } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import { injectIntl, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItem } from '@atlaskit/dropdown-menu-12';
import MoreIcon from '@atlaskit/icon/glyph/more';
// @ts-ignore TODO: upgrade package once AK provides types to fix noImplicitAny error here
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { ItemGroup } from '@atlaskit/item';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { DiffMetadataProps } from 'src/components/diff-set/diff-set';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useIntl } from 'src/hooks/intl';
import { getGlobalIsAnnotationsEnabled } from 'src/redux/diff-settings';
import {
  COLLAPSE_ALL_DIFFS,
  EXPAND_ALL_DIFFS,
} from 'src/redux/pull-request/actions';
import {
  getAllDiffFiles,
  getIsSingleFileModeActive,
} from 'src/redux/pull-request/selectors';

import { DiffFeaturesContext } from '../diff-features-context';
import messages from '../diff.i18n';
import { useReviewedFiles } from '../reviewed-files/reviewed-files-provider';
import { useViewEntireFile } from '../view-entire-file/hooks';

import { ToggleAnnotationsItem } from './action-items/toggle-annotations-item';
import { ToggleCommentsItem } from './action-items/toggle-comments-item';
import { ToggleSideBySideItem } from './action-items/toggle-side-by-side-item';
import { ViewSourceItem } from './action-items/view-source-item';
import CollapseAllDropdownItem from './collapse-all-dropdown-item';
import ExpandAllDropdownItem from './expand-all-dropdown-item';

const ItemGroupWrapper = styled.div({
  borderBottom: `1px solid ${token('color.border', colors.N40)}`,
  '&:last-child': {
    borderBottom: 'none',
  },
});

type DiffActionsItemGroupProps = {
  children: ReactNode;
  title?: string;
};

const DiffActionsItemGroup = React.memo((props: DiffActionsItemGroupProps) => {
  const { children, title } = props;

  // We want a DropdownItemGroup without a visible `title`
  // `DropdownItemGroup` only supports `aria-label` via the `title` prop
  return (
    <ItemGroupWrapper>
      <ItemGroup label={title} role="menu">
        {children}
      </ItemGroup>
    </ItemGroupWrapper>
  );
});

type DiffActionsMenuProps = {
  diffMetadataProps: DiffMetadataProps;
  filepath: string;
  isDeleted?: boolean;
  isSideBySide?: boolean;
  isViewEntireFileSupported?: boolean;
  isCollapsible?: boolean;
  onAddComment?: () => void;
  onDiffActionsMenuClicked?: (e: { isOpen: boolean }) => void;
  onSideBySide?: () => void;
};

const DiffActionsMenu = React.memo(
  ({
    diffMetadataProps,
    filepath,
    onDiffActionsMenuClicked,
    onSideBySide,
    isDeleted,
    isSideBySide,
    isViewEntireFileSupported = false,
    isCollapsible = true,
  }: DiffActionsMenuProps) => {
    const intl = useIntl();
    const dispatch = useDispatch();

    const onViewEntireFile = useViewEntireFile(
      filepath,
      diffMetadataProps.sourceHash,
      diffMetadataProps.sourceRepoFullSlug,
      diffMetadataProps.destinationHash,
      diffMetadataProps.destinationRepoFullSlug
    );

    const { hasAnnotations, hasComments, hasMarkFilesAsViewed } =
      useContext(DiffFeaturesContext);

    const isPrAnnotationsEnabled =
      useSelector(getGlobalIsAnnotationsEnabled) && hasAnnotations;
    const isSingleFileModeActive = useSelector(getIsSingleFileModeActive);

    const { unmarkAllViewed, markAllViewed } = useReviewedFiles();
    const allDiffFiles = useSelector(getAllDiffFiles);

    const unmarkAllFilesViewed = () => {
      unmarkAllViewed();
      dispatch({ type: EXPAND_ALL_DIFFS });
    };

    const markAllFilesViewed = () => {
      markAllViewed(allDiffFiles);
      if (!isSingleFileModeActive) {
        dispatch({ type: COLLAPSE_ALL_DIFFS });
      }
    };

    const showMarkAllFilesOptions = useStatsigGate(
      StatsigFeatureKeys.markAllFiles
    );

    return (
      <DropdownMenu
        placement="bottom-end"
        spacing="compact"
        trigger={({ triggerRef, ...props }) => (
          <Button
            {...props}
            iconBefore={
              <MoreIcon
                label={intl.formatMessage(messages.diffActionsButtonLabel)}
              />
            }
            ref={triggerRef}
            appearance="subtle"
          />
        )}
        onOpenChange={onDiffActionsMenuClicked}
      >
        <DiffActionsItemGroup title={intl.formatMessage(messages.thisFile)}>
          {onSideBySide && (
            <ToggleSideBySideItem
              onSideBySide={onSideBySide}
              isSideBySide={!!isSideBySide}
            />
          )}
          {isViewEntireFileSupported && (
            <DropdownItem
              data-qa="pr-diff-file-view-entire-file"
              key="file-actions-menu-view-entire-file"
              onClick={onViewEntireFile}
            >
              <FormattedMessage {...messages.viewEntireFile} />
            </DropdownItem>
          )}
          <ViewSourceItem
            sourceHash={diffMetadataProps.sourceHash}
            destinationHash={diffMetadataProps.destinationHash}
            sourceRepoFullSlug={diffMetadataProps.sourceRepoFullSlug}
            destinationRepoFullSlug={diffMetadataProps.destinationRepoFullSlug}
            isDeleted={isDeleted}
            filepath={filepath}
          />
          {isPrAnnotationsEnabled ? (
            <ToggleAnnotationsItem filepath={filepath} />
          ) : null}
          {hasComments ? <ToggleCommentsItem filepath={filepath} /> : null}
        </DiffActionsItemGroup>
        {isCollapsible && (
          <DiffActionsItemGroup title={intl.formatMessage(messages.allFiles)}>
            <CollapseAllDropdownItem key="file-actions-menu-collapse-all">
              <FormattedMessage {...messages.collapseAll} />
            </CollapseAllDropdownItem>
            <ExpandAllDropdownItem key="file-actions-menu-expand-all">
              <FormattedMessage {...messages.expandAll} />
            </ExpandAllDropdownItem>
            {hasMarkFilesAsViewed && showMarkAllFilesOptions && (
              <>
                <DropdownItem onClick={markAllFilesViewed}>
                  <FormattedMessage {...messages.markAllViewed} />
                </DropdownItem>
                <DropdownItem onClick={unmarkAllFilesViewed}>
                  <FormattedMessage {...messages.unmarkAllViewed} />
                </DropdownItem>
              </>
            )}
          </DiffActionsItemGroup>
        )}
      </DropdownMenu>
    );
  }
);

export const BaseDiffActionsMenu = injectIntl(DiffActionsMenu);
